import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  BarChart3, 
  Receipt, 
  TrendingUp, 
  CreditCard, 
  FileText,
  Users, 
  User,
  LogOut,
  X
} from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';
import { APP_NAME } from '../../../constants/appConstants';

const Sidebar = ({ sidebarOpen, setSidebarOpen }) => {
  const location = useLocation();
  const { logout } = useAuth();

  // Navigation items for sidebar
  const mainNavItems = [
    { path: "/dashboard", label: "Dashboard", icon: <BarChart3 className="h-5 w-5" /> },
    { path: "/dashboard/invitations", label: "Collaborators", icon: <Users className="h-5 w-5" /> },
    { path: "/dashboard/profile-page", label: "Profile", icon: <User className="h-5 w-5" /> },
  ];

  const reportNavItems = [
    { path: "/dashboard/expense-reports", label: "Expense Reports", icon: <Receipt className="h-5 w-5" /> },
    { path: "/dashboard/revenue-reports", label: "Revenue Reports", icon: <TrendingUp className="h-5 w-5" /> },
    { path: "/dashboard/bills", label: "Bills", icon: <CreditCard className="h-5 w-5" /> },
    { path: "/dashboard/proformas", label: "Proformas", icon: <FileText className="h-5 w-5" /> },
  ];

  // Check if a path is active
  const isActive = (path) => {
    if (path === '/dashboard' && location.pathname === '/dashboard') {
      return true;
    }
    return location.pathname.startsWith(path) && path !== '/dashboard';
  };

  return (
    <aside 
      className={`fixed md:sticky md:top-0 z-30 flex flex-col w-64 h-screen bg-white border-r border-gray-200 shadow-sm transition-transform duration-300 transform ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
      }`}
    >
      <div className="p-5 border-b border-gray-200 flex justify-between items-center flex-shrink-0">
        <div>
          <h1 className="text-xl font-bold text-gray-800">{APP_NAME}</h1>
          <p className="text-sm text-tertiary-500">Financial Management</p>
        </div>
        <button 
          onClick={() => setSidebarOpen(false)}
          className="md:hidden text-gray-500 hover:text-gray-700"
        >
          <X className="h-6 w-6" />
        </button>
      </div>
      
      <div className="flex flex-col flex-1 overflow-y-auto">
        <nav className="flex-1 py-6 px-4">
          <div className="mb-6">
            <h3 className="text-xs uppercase font-semibold text-gray-500 tracking-wider mb-3 px-3">Main</h3>
            
            {mainNavItems.map((item) => (
              <Link 
                key={item.path}
                to={item.path} 
                className={`flex items-center px-3 py-2.5 mb-1 rounded-lg group ${
                  isActive(item.path) 
                    ? 'text-gray-900 bg-secondary-50' 
                    : 'text-gray-700 hover:bg-secondary-100'
                }`}
              >
                <span className={`mr-3 ${isActive(item.path) ? 'text-primary-600' : 'text-gray-500 group-hover:text-gray-700'}`}>
                  {item.icon}
                </span>
                <span className={isActive(item.path) ? 'font-medium' : ''}>{item.label}</span>
              </Link>
            ))}
          </div>
          
          <div>
            <h3 className="text-xs uppercase font-semibold text-gray-500 tracking-wider mb-3 px-3">Reports</h3>
            
            {reportNavItems.map((item) => (
              <Link 
                key={item.path}
                to={item.path} 
                className={`flex items-center px-3 py-2.5 mb-1 rounded-lg group ${
                  isActive(item.path) 
                    ? 'text-gray-900 bg-secondary-50' 
                    : 'text-gray-700 hover:bg-secondary-100'
                }`}
              >
                <span className={`mr-3 ${isActive(item.path) ? 'text-primary-600' : 'text-gray-500 group-hover:text-gray-700'}`}>
                  {item.icon}
                </span>
                <span className={isActive(item.path) ? 'font-medium' : ''}>{item.label}</span>
              </Link>
            ))}
          </div>
        </nav>
      </div>
      
      <div className="p-4 border-t border-gray-200 flex-shrink-0">
        <button 
          onClick={logout} 
          className="flex items-center w-full px-3 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
        >
          <LogOut className="h-5 w-5 mr-3 text-gray-500" />
          <span>Logout</span>
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;